import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Alert, CircularProgress, Box, Divider } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { getClientBalance, type ClientListItem } from '../../api/clienti'

function formatAmount(value: number) {
  return value.toFixed(2).replace('.', ',')
}

export default function ClientBalanceDialog({ client, onClose }: { client: ClientListItem | null; onClose: () => void }) {
  const open = client !== null
  const { data, isLoading } = useQuery({
    queryKey: ['comercial', 'clienti', client?.id, 'sold'],
    queryFn: () => getClientBalance(client!.id),
    enabled: open,
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Sold client — {client?.name}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        )}
        {data && (
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Row label="Facturi emise, neîncasate" value={data.unpaidInvoicesTotal} />
            <Row label="Comenzi confirmate, neîncă facturate" value={data.pendingOrdersTotal} />
            <Divider />
            <Row label="Expunere totală" value={data.totalExposure} bold />
            <Row label="Limită de credit" value={data.creditLimit} placeholder="Nesetată" />
            {data.availableCredit != null && (
              <Row label="Credit disponibil" value={data.availableCredit} tone={data.availableCredit < 0 ? 'error' : undefined} />
            )}
            {data.availableCredit != null && data.availableCredit < 0 && (
              <Alert severity="warning">Limita de credit este depășită — comenzile noi vor cere suprascriere explicită la confirmare.</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}

function Row({ label, value, bold, placeholder, tone }: {
  label: string; value: number | null; bold?: boolean; placeholder?: string; tone?: 'error'
}) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant={bold ? 'subtitle1' : 'body2'} color={tone === 'error' ? 'error.main' : undefined}
        sx={{ fontFamily: '"IBM Plex Mono", monospace', fontWeight: bold ? 600 : undefined }}>
        {value == null ? (placeholder ?? '—') : formatAmount(value)}
      </Typography>
    </Stack>
  )
}
